import ChevronsRightIcon from "@/app/shared-components/icons/ChevronsRightIcon";

interface HowItWorksProps {
  onGetBotbPass: () => void;
  onBuyTickets: () => void;
}

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="size-4 shrink-0">
    <path
      d="M8 6H8.00667M7.33333 8H8V10.6667H8.66667M2 8C2 8.78793 2.15519 9.56815 2.45672 10.2961C2.75825 11.0241 3.20021 11.6855 3.75736 12.2426C4.31451 12.7998 4.97595 13.2417 5.7039 13.5433C6.43185 13.8448 7.21207 14 8 14C8.78793 14 9.56815 13.8448 10.2961 13.5433C11.0241 13.2417 11.6855 12.7998 12.2426 12.2426C12.7998 11.6855 13.2417 11.0241 13.5433 10.2961C13.8448 9.56815 14 8.78793 14 8C14 6.4087 13.3679 4.88258 12.2426 3.75736C11.1174 2.63214 9.5913 2 8 2C6.4087 2 4.88258 2.63214 3.75736 3.75736C2.63214 4.88258 2 6.4087 2 8Z"
      stroke="#898994"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepBadgeMobile = ({ number }: { number: string }) => (
  <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-primary">
    <span className="font-jost text-[16px] font-semibold leading-[22px] text-primary">{number}</span>
  </div>
);

const StepBadgeDesktop = ({ number }: { number: string }) => (
  <div className="relative z-[1] flex size-[78px] items-center justify-center rounded-full bg-white shadow-[1px_10px_50px_0px_rgba(255,130,0,0.2)]">
    <span className="font-jost text-[32px] font-semibold leading-none text-primary">{number}</span>
  </div>
);

const HowItWorks = ({ onGetBotbPass, onBuyTickets }: HowItWorksProps) => {
  return (
    <div className="w-full overflow-hidden bg-gradient-to-b from-[#f5f5f5] to-transparent pb-[41px] pt-5 lg:pb-12 lg:pt-10">
      <div className="mx-auto max-w-[1276px] px-5 lg:px-4">
        {/* ── Mobile layout ──────────────────────────────────── */}
        <div className="flex flex-col gap-[18px] lg:hidden">
          <div className="text-left font-jost text-[20px] font-bold leading-[26px] text-[#28282b]">
            How it <span className="text-primary">works</span>
          </div>

          {/* Step 1 */}
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <StepBadgeMobile number="1" />
              <div className="flex flex-col gap-1">
                <div className="text-left font-jost text-[16px] font-medium leading-[22px] text-[#4c4c52]">
                  Enter our competitions
                </div>
                <div className="font-jost text-[13px] font-normal leading-[18px] text-[#6a7282]">
                  Pick the prizes you want to go for and enter. There are three ways to play:
                </div>
              </div>
            </div>

            {/* Card indented to align with text (36px badge + 12px gap) */}
            <div className="ml-[48px] rounded-[12px] border border-black/[0.08] p-4 max-lg:max-w-[300px]">
              <div className="flex flex-col gap-4">
                <div className="relative">
                  <span className="absolute right-2 -top-1.5 bg-[#4d00ff] text-white text-[9px] font-semibold px-2 h-3 flex items-center rounded tracking-[0.3px] uppercase pt-px">
                    RECOMMENDED
                  </span>
                  <button
                    onClick={onGetBotbPass}
                    className="flex h-9 w-full items-center justify-center gap-2 rounded bg-primary">
                    <span className="whitespace-nowrap font-jost text-[13px] font-medium leading-[18px] text-white">
                      ★ BOTB Pass from £9.99/mo
                    </span>
                    <ChevronsRightIcon variant={2} className="size-3 text-white" />
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute right-2 -top-1.5 bg-[#08a200] text-white text-[9px] font-semibold px-2 h-3 flex items-center rounded tracking-[0.3px] uppercase pt-px">
                    NO COMMITMENT
                  </span>
                  <button
                    onClick={onBuyTickets}
                    className="flex h-9 w-full items-center justify-center gap-2 rounded bg-[#fff0e0]">
                    <span className="whitespace-nowrap font-jost text-[13px] font-medium leading-[18px] text-primary">
                      Buy tickets directly
                    </span>
                    <ChevronsRightIcon variant={2} className="size-3 text-primary" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1">
                <span className="flex items-center gap-1 font-jost text-[13px] font-medium leading-[18px] text-[#696971]">
                  <InfoIcon />
                  Free postal entry
                </span>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-3">
            <StepBadgeMobile number="2" />
            <div className="flex flex-col gap-1">
              <div className="text-left font-jost text-[16px] font-medium leading-[22px] text-[#4c4c52]">
                See if you&apos;ve won
              </div>
              <div className="font-jost text-[13px] font-normal leading-[18px] text-[#6a7282]">
                We announce tens of winners every week. Check your account daily and turn on notifications to see if
                you&apos;ve won — or answer the door, it could be Christian and the BOTB team!
              </div>
            </div>
          </div>
        </div>

        {/* ── Desktop layout ─────────────────────────────────── */}
        <div className="hidden lg:flex lg:items-start">
          {/* Heading: two stacked lines on the left */}
          <div className="w-[178px] shrink-0 pt-1">
            <div className="text-left font-jost text-[32px] font-bold leading-[40px] text-[#28282b]">How it</div>
            <div className="text-left font-jost text-[32px] font-bold leading-[40px] text-primary">works</div>
          </div>

          {/* Steps area with connecting line */}
          <div className="relative flex-1">
            {/* Horizontal connecting line through badge centres (78px / 2 = 39px) */}
            <div className="absolute left-0 right-0 top-[39px] h-px bg-[#FF8200] w-screen" />

            {/* Two steps */}
            <div className="flex gap-[204px]">
              {/* Step 1 */}
              <div className="ml-[109px] flex flex-1 flex-col gap-8 max-w-[352px]">
                <StepBadgeDesktop number="1" />

                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-1">
                    <div className="text-left font-jost text-[24px] font-bold leading-[30px] text-[#4c4c52]">
                      Enter our competitions
                    </div>
                    <div className="font-jost text-[16px] font-normal leading-[22px] text-[#6a7282]">
                      Pick the prizes you want to go for and enter. There are three ways to play:
                    </div>
                  </div>

                  <div className="max-w-[352px] rounded-[12px] border border-black/[0.08] px-4 py-5">
                    <div className="flex flex-col gap-5">
                      <div className="relative">
                        <span className="absolute right-0 -top-2 bg-[#4d00ff] text-white text-[9px] font-semibold px-2 h-3 flex items-center rounded tracking-[0.3px] uppercase">
                          RECOMMENDED
                        </span>
                        <button
                          onClick={onGetBotbPass}
                          className="flex h-[40px] w-full items-center justify-center gap-2 rounded bg-primary">
                          <span className="whitespace-nowrap font-jost text-[13px] font-medium leading-[18px] text-white">
                            ★ BOTB Pass from £9.99/mo
                          </span>
                          <ChevronsRightIcon variant={2} className="size-3 text-white" />
                        </button>
                      </div>
                      <div className="relative">
                        <span className="absolute right-0 -top-2 bg-[#08a200] text-white text-[9px] font-semibold px-2 h-3 flex items-center rounded tracking-[0.3px] uppercase">
                          NO COMMITMENT
                        </span>
                        <button
                          onClick={onBuyTickets}
                          className="flex h-[40px] w-full items-center justify-center gap-2 rounded bg-[#fff0e0]">
                          <span className="whitespace-nowrap font-jost text-[13px] font-medium leading-[18px] text-primary">
                            Buy tickets directly
                          </span>
                          <ChevronsRightIcon variant={2} className="size-3 text-primary" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1">
                      <span className="flex items-center gap-1 font-jost text-[13px] font-medium leading-[18px] text-[#696971]">
                        <InfoIcon />
                        Free postal entry
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-1 flex-col gap-8 max-w-[371px]">
                <StepBadgeDesktop number="2" />

                <div className="flex flex-col gap-1">
                  <div className="text-left font-jost text-[24px] font-bold leading-[30px] text-[#4c4c52]">
                    See if you&apos;ve won
                  </div>
                  <div className="font-jost text-[16px] font-normal leading-[22px] text-[#6a7282]">
                    We announce tens of winners every week. Check your account daily and turn on notifications to see if
                    you&apos;ve won — or answer the door, it could be Christian and the BOTB team!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
